import React, { useState } from "react";
import { Button } from "antd";
import { useLocation } from "react-router-dom";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { cueEditUrl } from "../SubtitlesBlock/linkTarget";
import {
    findMarkedRow,
    MarkedSame,
    markedSameCues,
    markedSameFollowLine,
    markedSameOf,
    markedSameQuestion,
    markedSameState,
    markedSameWhat,
    sameLang,
    StreamFix,
} from "./model";

/** Переход на другую папку — полной загрузкой страницы, как у списка папок (тест подменяет `go`). */
export const pageNav = {
    go: (url: string): void => {
        window.location.href = url;
    },
};

interface Props {
    fix: StreamFix;
    /** Заявки полосы: по ним — вписал ли переводчик уже свой текст (новая правка тех же реплик). */
    fixes?: StreamFix[];
    /** Кнопки «К реплике» — только у своей правки. */
    mine?: boolean;
    /** Строка отчёта уже над ним (окно «папки и правки»): «не исправлено …» не повторять — только вопрос и кнопки. */
    compact?: boolean;
}

/** Надпись кнопки: один кусок — «К реплике»; несколько — с номером куска. */
const goTitle = (items: MarkedSame[], item: MarkedSame, index: number): string => {
    if (items.length === 1) return "К реплике";
    return item.index && item.of ? `К реплике: кусок ${item.index} из ${item.of}` : `К реплике ${index + 1}`;
};

/**
 * «Не исправлено — вопрос вам» (проба P50m20f, E5; проверка круга AF): кусок (реплика), на который переводчик показал
 * «неверный перевод», перевод заново оставил прежним. Видно, что осталось прежним и почему, вопрос — вписать свой текст
 * и «К реплике»: в этой папке — лента едет к куску, строка выделена, курсор в поле его перевода (как обычная правка
 * текста реплики); в другой папке — открывается та папка на этом куске с той же правкой текста. Вписан свой текст
 * (новая правка тех же реплик) — вопрос закрыт, кнопок нет.
 */
export const MarkedSameAsk: React.FC<Props> = ({ fix, fixes, mine = true, compact = false }) => {
    const state = useAudioStateContext() as
        | { folder?: number | string; lang?: string; originalLang?: string; translationTrackAll?: Record<string, any[]> }
        | undefined;
    const handles = useAudioHandlesContext() as
        | { requestFocusCue?: (request: { id: string; folder: number }) => void }
        | undefined;
    const location = useLocation();
    const [note, setNote] = useState("");

    const all = fixes && fixes.length ? fixes : [fix];
    const items = markedSameOf(fix);
    const phase = markedSameState(fix, all).phase;
    if (!items.length || phase === "none") return null;
    if (phase !== "asking") {
        return (
            <div data-testid="marked-same-follow" data-phase={phase} style={{ fontSize: 12, color: "#595959" }}>
                {markedSameFollowLine(fix, all)}
            </div>
        );
    }

    const folder = Number(fix.folder || 0);
    const here = Boolean(state) && Number(state?.folder) === folder && sameLang(state?.lang, fix.lang);

    const goTo = (item: MarkedSame) => {
        setNote("");
        const text = String(item.text || "").trim();
        if (here) {
            const rows = state?.translationTrackAll?.[String(state?.originalLang || "")];
            const row = findMarkedRow(rows, item);
            if (!row || !handles?.requestFocusCue) {
                setNote(
                    `Куска «${text}» на ленте этой папки сейчас нет — папка ещё не перечитана; попробуйте через ` +
                        "несколько секунд.",
                );
                return;
            }
            handles.requestFocusCue({ id: String(row.original_id || row.id), folder });
            return;
        }
        const cue = markedSameCues(item)[0];
        if (!cue || !folder) {
            setNote(`Отчёт не назвал кусок по имени — откройте папку ${fix.folder} и найдите «${text}».`);
            return;
        }
        const lang = state?.lang && sameLang(state.lang, fix.lang) ? state.lang : String(fix.lang || state?.lang || "");
        pageNav.go(cueEditUrl(location.pathname, location.search, folder, lang, cue));
    };

    return (
        <div data-testid="marked-same" style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4 }}>
            {!compact &&
                items.map((item, index) => (
                    <div key={`${item.cue || ""}-${item.now || ""}-${index}`} data-testid="marked-same-unfixed">
                        Не исправлено: {markedSameWhat(item)}
                    </div>
                ))}
            <div data-testid="marked-same-question">{markedSameQuestion(items)}</div>
            {mine && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {items.map((item, index) => (
                        <Button
                            key={`${item.cue || ""}-${item.now || ""}-${index}`}
                            size="small"
                            type={index === 0 ? "primary" : "default"}
                            data-testid="marked-same-go"
                            data-cue={markedSameCues(item)[0] || ""}
                            onClick={() => goTo(item)}
                        >
                            {goTitle(items, item, index)}
                        </Button>
                    ))}
                    {!here && <span style={{ color: "#8c8c8c" }}>откроется папка {fix.folder}</span>}
                </div>
            )}
            {note && (
                <div data-testid="marked-same-note" style={{ color: "#cf1322" }}>
                    {note}
                </div>
            )}
        </div>
    );
};
